import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Calendar as CalendarIcon, FileText, Package, ArrowRight, Loader2, Video, RefreshCw } from "lucide-react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  confirmed: "bg-green-50 text-green-700 border border-green-200",
  pending_payment: "bg-yellow-50 text-yellow-700 border border-yellow-200",
  cancelled: "bg-red-50 text-red-600 border border-red-200",
  completed: "bg-navy/5 text-navy border border-navy/20",
};

const CANCELLABLE = ["confirmed", "pending_payment"];

// Returns true if the booking is more than 24h away
function isReschedulable(booking) {
  if (!["confirmed"].includes(booking.status)) return false;
  try {
    const [h, mAmPm] = booking.time_slot.split(":");
    const [min, ampm] = mAmPm.trim().split(" ");
    let hour = parseInt(h, 10);
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    const dt = new Date(`${booking.date}T${String(hour).padStart(2, "0")}:${min}:00`);
    return (dt - Date.now()) > 86400000;
  } catch { return true; }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Reschedule modal state
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(undefined);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleSlot, setRescheduleSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  // Intentional: empty dep array fetches bookings and materials once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/bookings`, { withCredentials: true }),
      axios.get(`${API}/materials`, { withCredentials: true }),
    ]).then(([bRes, mRes]) => {
      setBookings(bRes.data);
      setMaterials(mRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Fetch available slots when reschedule date changes
  useEffect(() => {
    if (!rescheduleDate || !rescheduleTarget) return;
    const iso = rescheduleDate.toISOString().split("T")[0];
    setSlotsLoading(true);
    setRescheduleSlot(null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/availability/${iso}`)
      .then(r => r.json())
      .then(d => { setRescheduleSlots(d.slots || []); setSlotsLoading(false); })
      .catch(() => { setRescheduleSlots([]); setSlotsLoading(false); });
  }, [rescheduleDate, rescheduleTarget]);

  async function handleConfirmCancel() {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await axios.delete(`${API}/bookings/${cancelTarget._id}`, { withCredentials: true });
      setBookings(prev =>
        prev.map(b => b._id === cancelTarget._id ? { ...b, status: "cancelled" } : b)
      );
      toast.success("Session cancelled successfully.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to cancel. Please try again or contact us directly.";
      toast.error(msg);
    } finally {
      setCancelling(false);
      setCancelTarget(null);
    }
  }

  async function handleConfirmReschedule() {
    if (!rescheduleTarget || !rescheduleDate || !rescheduleSlot) return;
    setRescheduling(true);
    try {
      const iso = rescheduleDate.toISOString().split("T")[0];
      const res = await axios.put(
        `${API}/bookings/${rescheduleTarget._id}/reschedule`,
        { new_date: iso, new_time_slot: rescheduleSlot },
        { withCredentials: true }
      );
      setBookings(prev =>
        prev.map(b => b._id === rescheduleTarget._id ? { ...b, ...res.data.booking } : b)
      );
      toast.success("Session rescheduled. A new confirmation email has been sent.");
      setRescheduleTarget(null);
      setRescheduleDate(undefined);
      setRescheduleSlot(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to reschedule. Please try again or contact us directly.";
      toast.error(msg);
    } finally {
      setRescheduling(false);
    }
  }

  const isPastOrWeekend = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    // Allow any day of week — Julia decides via admin availability settings
    return d < today;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-navy/40" />
      </div>
    );
  }

  const confirmedBookings = bookings.filter(b => b.status === "confirmed" || b.status === "completed");
  const pendingBookings = bookings.filter(b => b.status === "pending_payment");

  return (
    <div className="min-h-screen bg-ivory" data-testid="dashboard-page">

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={open => { if (!open) setCancelTarget(null); }}>
        <AlertDialogContent className="bg-ivory border border-soft-gray rounded-none max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-lora text-navy text-xl font-normal">
              Cancel this session?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-serif text-navy/60 text-sm leading-relaxed">
              You are about to cancel{" "}
              <span className="text-navy font-semibold">{cancelTarget?.service_name}</span>{" "}
              on {cancelTarget?.date} at {cancelTarget?.time_slot}.
              <br /><br />
              This action cannot be undone. Cancellations must be made at least 24 hours in advance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel
              disabled={cancelling}
              className="font-serif text-sm bg-transparent border border-soft-gray text-navy hover:bg-soft-gray/30 rounded-none"
              data-testid="cancel-dialog-keep"
            >
              Keep Session
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelling}
              onClick={handleConfirmCancel}
              className="font-serif text-sm bg-red-700 text-white hover:bg-red-800 border-0 rounded-none"
              data-testid="cancel-dialog-confirm"
            >
              {cancelling ? "Cancelling…" : "Yes, Cancel Session"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule Modal */}
      <AlertDialog
        open={!!rescheduleTarget}
        onOpenChange={open => {
          if (!open) { setRescheduleTarget(null); setRescheduleDate(undefined); setRescheduleSlot(null); }
        }}
      >
        <AlertDialogContent className="bg-ivory border border-soft-gray rounded-none max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-lora text-navy text-xl font-normal">
              Reschedule Session
            </AlertDialogTitle>
            <AlertDialogDescription className="font-serif text-navy/60 text-sm leading-relaxed">
              Rescheduling <span className="text-navy font-semibold">{rescheduleTarget?.service_name}</span>{" "}
              (currently {rescheduleTarget?.date} at {rescheduleTarget?.time_slot}).
              Select a new date and time at least 24 hours in advance.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-2 space-y-4">
            <Calendar
              mode="single"
              selected={rescheduleDate}
              onSelect={setRescheduleDate}
              disabled={isPastOrWeekend}
              className="rounded-none border border-soft-gray bg-white mx-auto"
              data-testid="reschedule-calendar"
            />
            {rescheduleDate && (
              <div>
                <p className="text-xs tracking-widest uppercase text-navy/40 mb-2 font-sans">
                  Available Times — {rescheduleDate.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}
                </p>
                {slotsLoading ? (
                  <div className="flex items-center gap-2 text-navy/40 text-sm py-2">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                  </div>
                ) : rescheduleSlots.length === 0 ? (
                  <p className="text-navy/40 font-serif text-sm py-2">No available times on this date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="reschedule-slots">
                    {rescheduleSlots.map(slot => (
                      <button
                        key={slot}
                        onClick={() => setRescheduleSlot(slot)}
                        className={`py-2 text-sm font-serif border transition-all ${
                          rescheduleSlot === slot
                            ? "bg-navy text-ivory border-navy"
                            : "bg-white text-navy border-soft-gray hover:border-navy/40"
                        }`}
                        data-testid="reschedule-slot-btn"
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <AlertDialogFooter className="gap-3 mt-4">
            <AlertDialogCancel
              disabled={rescheduling}
              className="font-serif text-sm bg-transparent border border-soft-gray text-navy hover:bg-soft-gray/30 rounded-none"
              data-testid="reschedule-dialog-cancel"
            >
              Keep Current Time
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={rescheduling || !rescheduleDate || !rescheduleSlot}
              onClick={handleConfirmReschedule}
              className="font-serif text-sm bg-navy text-ivory hover:bg-navy/90 border-0 rounded-none disabled:opacity-40"
              data-testid="reschedule-dialog-confirm"
            >
              {rescheduling ? "Rescheduling…" : "Confirm New Time"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="bg-navy px-6 lg:px-12 py-16">
        <div className="max-w-6xl mx-auto">
          <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-2">Client Portal</p>
          <h1 className="font-lora text-3xl text-ivory font-normal">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-ivory/50 font-serif text-sm mt-1">Your advisory journey, at a glance.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b border-soft-gray">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-6 grid grid-cols-3 divide-x divide-soft-gray">
          <div className="pr-6 text-center">
            <p className="font-lora text-2xl text-navy">{confirmedBookings.length}</p>
            <p className="text-navy/40 text-xs tracking-widest uppercase mt-1">Confirmed Sessions</p>
          </div>
          <div className="px-6 text-center">
            <p className="font-lora text-2xl text-navy">{pendingBookings.length}</p>
            <p className="text-navy/40 text-xs tracking-widest uppercase mt-1">Pending</p>
          </div>
          <div className="pl-6 text-center">
            <p className="font-lora text-2xl text-navy">{materials.length}</p>
            <p className="text-navy/40 text-xs tracking-widest uppercase mt-1">Materials</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white border border-soft-gray p-1 w-fit">
          {[
            { id: "bookings", label: "Bookings", icon: CalendarIcon },
            { id: "services", label: "Services", icon: Package },
            { id: "materials", label: "Materials", icon: FileText },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm tracking-wide transition-all ${
                tab === t.id ? "bg-navy text-ivory" : "text-navy/50 hover:text-navy"
              }`}
              data-testid={`dashboard-tab-${t.id}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* Bookings Tab */}
        {tab === "bookings" && (
          <div data-testid="dashboard-bookings">
            {bookings.length === 0 ? (
              <div className="text-center py-20 bg-white border border-soft-gray">
                <CalendarIcon size={32} className="text-navy/20 mx-auto mb-4" />
                <h3 className="font-lora text-xl text-navy font-normal mb-2">No Sessions Yet</h3>
                <p className="text-navy/40 font-serif text-sm mb-6">Ready to gain clarity? Book your first advisory session.</p>
                <Link
                  to="/booking"
                  className="inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3 text-sm tracking-widest uppercase hover:bg-navy/90 transition-all"
                >
                  Book a Session <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map(b => {
                  const isPaid = b.payment_status === "paid" || (b.total > 0 && b.status === "confirmed");
                  const canReschedule = isReschedulable(b);
                  return (
                    <div key={b._id || b.payment_session_id} className="bg-white border border-soft-gray p-6" data-testid="booking-card">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-lora text-navy text-base mb-1">{b.service_name}</p>
                          <p className="text-navy/50 font-serif text-sm">{b.date} · {b.time_slot}</p>
                          {b.zoom_join_url && b.status === "confirmed" && (
                            <a
                              href={b.zoom_join_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 mt-2 text-xs text-navy/70 hover:text-navy border border-navy/20 hover:border-navy/40 px-3 py-1.5 transition-all"
                              data-testid="zoom-join-link"
                            >
                              <Video size={12} /> Join Zoom Meeting
                            </a>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <span
                            className={`inline-block text-xs px-3 py-1 rounded-sm mb-2 ${STATUS_STYLES[b.status] || "bg-gray-50 text-gray-600"}`}
                            data-testid="booking-status"
                          >
                            {b.status?.replace("_", " ")}
                          </span>
                          <p className="font-lora text-navy">${b.total} {b.currency}</p>

                          {CANCELLABLE.includes(b.status) && (
                            <div className="flex flex-col items-end gap-1 mt-2">
                              {/* Reschedule button — always shown for reschedulable bookings */}
                              {canReschedule && (
                                <button
                                  onClick={() => setRescheduleTarget(b)}
                                  className="flex items-center gap-1 text-xs text-navy/50 hover:text-navy transition-colors"
                                  data-testid="reschedule-booking-btn"
                                >
                                  <RefreshCw size={11} /> Reschedule
                                </button>
                              )}

                              {/* Cancel: paid → mailto link, free → API cancel */}
                              {isPaid ? (
                                <a
                                  href={`mailto:julia@denicolaconsulting.com?subject=Cancellation%20Request%20%E2%80%94%20${encodeURIComponent(b.service_name)}&body=Hi%20Julia%2C%0A%0AI%20would%20like%20to%20request%20cancellation%20of%20my%20session%3A%0A%0AService%3A%20${encodeURIComponent(b.service_name)}%0ADate%3A%20${encodeURIComponent(b.date)}%0ATime%3A%20${encodeURIComponent(b.time_slot)}%0A%0APlease%20let%20me%20know%20next%20steps.%0A%0AThank%20you.`}
                                  className="text-xs text-navy/30 hover:text-navy/60 transition-colors underline underline-offset-2"
                                  data-testid="cancel-paid-link"
                                >
                                  Request cancellation
                                </a>
                              ) : (
                                <button
                                  onClick={() => setCancelTarget(b)}
                                  className="text-xs text-navy/30 hover:text-red-600 transition-colors underline underline-offset-2"
                                  data-testid="cancel-booking-btn"
                                >
                                  Cancel session
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-6">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 text-navy/50 text-sm tracking-widest uppercase hover:text-navy transition-colors"
              >
                Book Another Session <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {tab === "services" && (
          <div data-testid="dashboard-services">
            {confirmedBookings.length === 0 ? (
              <div className="text-center py-20 bg-white border border-soft-gray">
                <Package size={32} className="text-navy/20 mx-auto mb-4" />
                <p className="font-lora text-xl text-navy font-normal mb-2">No Purchased Services</p>
                <p className="text-navy/40 font-serif text-sm">Your purchased services will appear here once confirmed.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {confirmedBookings.map(b => (
                  <div key={b._id} className="bg-white border border-soft-gray p-6">
                    <p className="font-lora text-xs tracking-widest uppercase text-gold mb-2">Active Service</p>
                    <h3 className="font-lora text-navy text-lg font-normal mb-1">{b.service_name}</h3>
                    <p className="text-navy/50 font-serif text-sm">{b.date} · {b.time_slot}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Materials Tab */}
        {tab === "materials" && (
          <div data-testid="dashboard-materials">
            {materials.length === 0 ? (
              <div className="text-center py-20 bg-white border border-soft-gray">
                <FileText size={32} className="text-navy/20 mx-auto mb-4" />
                <p className="font-lora text-xl text-navy font-normal mb-2">No Materials Yet</p>
                <p className="text-navy/40 font-serif text-sm">Materials shared by your advisor will appear here.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {materials.map(m => (
                  <div key={m._id} className="bg-white border border-soft-gray p-6">
                    <p className="font-lora text-navy text-base mb-1">{m.title}</p>
                    <p className="text-navy/50 font-serif text-sm mb-3">{m.description}</p>
                    {m.url && (
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gold text-xs tracking-widest uppercase hover:opacity-70 transition-opacity"
                      >
                        View Material &rarr;
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}